import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

/**
 * How a player is labelled, in the two lengths the report needs.
 *
 * The roster header has room for a word, so an opponent is just "Esquerda" —
 * the two of them sit side by side under the opposing team, and the pairing
 * makes the sense. Anywhere else a label travels alone (an award, a bar, a
 * legend), so it has to say "Oponente Esquerda" to stand on its own.
 */
export interface PlayerNameForms {
  short: string;
  long: string;
}

/**
 * Per-slot labels. Tournament mode supplies real names as plain strings (one
 * form for both lengths); the per-session report supplies role-derived forms
 * built from the segment's teams + roles.
 *
 * There is deliberately no fallback to the slot id: "player3" is a tracking
 * id from the inference pipeline and means nothing to a reader.
 */
export type PlayerNamesMap = Record<string, string | PlayerNameForms>;

const PlayerNamesContext = createContext<PlayerNamesMap | null>(null);

interface PlayerNamesProviderProps {
  names: PlayerNamesMap;
  children: ReactNode;
}

export const PlayerNamesProvider = ({
  names,
  children,
}: PlayerNamesProviderProps) => (
  <PlayerNamesContext.Provider value={names}>
    {children}
  </PlayerNamesContext.Provider>
);

export interface NameOptions {
  /** The header's compact form — "Esquerda" rather than "Oponente Esquerda". */
  short?: boolean;
}

const pick = (
  entry: string | PlayerNameForms | undefined,
  options?: NameOptions,
): string => {
  if (!entry) return '';
  if (typeof entry === 'string') return entry;
  return options?.short ? entry.short : entry.long;
};

/** Raw override lookup. Returns null when no label is set for this slot. */
export function usePlayerNameOverride(playerId: string): string | null {
  const map = useContext(PlayerNamesContext);
  return pick(map?.[playerId]) || null;
}

/** Resolve one player's display label. */
export function useResolvedPlayerName(
  playerId: string,
  options?: NameOptions,
): string {
  const map = useContext(PlayerNamesContext);
  const short = options?.short;
  return useMemo(
    () => pick(map?.[playerId], { short }),
    [map, playerId, short],
  );
}

/**
 * Like {@link useResolvedPlayerName} but returns a resolver function, so a
 * component can label many players (e.g. a comparison view) without breaking
 * the rules of hooks by calling a hook per player in a loop.
 */
export function usePlayerNameResolver(): (
  playerId: string,
  options?: NameOptions,
) => string {
  const map = useContext(PlayerNamesContext);
  return useCallback(
    (playerId: string, options?: NameOptions) => pick(map?.[playerId], options),
    [map],
  );
}
